/**
 * Convert Firebase SDK errors (Firestore, Auth, callable) into user-friendly toast text.
 * Prevents raw technical messages like "Missing or insufficient permissions." from leaking to the UI.
 */

const PERMISSION_RE = /missing or insufficient permissions|permission.denied/i;
const UNAVAILABLE_RE = /unavailable|deadline.exceeded|resource.exhausted/i;
const UNAUTHENTICATED_RE = /unauthenticated|not.authenticated/i;
const NETWORK_RE = /failed to fetch|network error|aborted|net::err/i;
const NOT_FOUND_RE = /not.found|no document to update/i;
const QUOTA_RE = /quota|rate.limit|too many requests/i;
const INTERNAL_RE = /^internal$|functions\/internal/i;

export default function firebaseErrorMessage(err, fallback = 'Something went wrong. Try again.') {
  if (!err) return fallback;

  const raw = typeof err === 'string' ? err : (err.message ?? '');
  const code = err?.code ?? '';
  const combined = `${code} ${raw}`;

  if (PERMISSION_RE.test(combined) || code === 'permission-denied')
    return 'You don\u2019t have permission for this action. Try signing out and back in.';

  if (UNAUTHENTICATED_RE.test(combined) || code === 'unauthenticated')
    return 'Your session expired. Please sign in again.';

  if (NETWORK_RE.test(combined) || INTERNAL_RE.test(raw) || INTERNAL_RE.test(code))
    return 'Could not reach the server. Check your connection and try again.';

  if (UNAVAILABLE_RE.test(combined))
    return 'The service is temporarily unavailable. Try again in a moment.';

  if (QUOTA_RE.test(combined))
    return 'Too many requests. Wait a moment and try again.';

  if (NOT_FOUND_RE.test(combined))
    return 'The requested item was not found. It may have been deleted.';

  return raw || fallback;
}
